import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AutomataService,
  AutomationInterface,
  IsAutomationStopped,
  RunningState,
  WorkflowStepInterface,
} from '@vendasta/automata';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { GalaxyPopoverModule, PopoverPositions } from '@vendasta/galaxy/popover';
import { combineLatest, Observable } from 'rxjs';
import { first, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { ERROR_ICON_COLOR, TRIGGER_STEP_ID, WARNING_ICON_COLOR } from '../../../common/constants';
import { isCustomIcon } from '../../../common/custom-icons';
import { TaskDefinitionsService } from '../../../data-services/task-definitions.service';
import { CurrentlyWaitingDialogComponent } from '../../../dialogs/currently-waiting-dialog/currently-waiting-dialog.component';
import { WORKFLOW_ACTIONS } from '../../component-loader/action-definitions';
import { DirectlyReferencedStepTaskDefinitionIds } from '../../component-loader/constants';
import { AutomationErrorHandlerService } from '../../services';
import { DisplayService } from '../common/display.service';
import { MatMenuModule } from '@angular/material/menu';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { NodeActionsService } from '../../services/node-actions.service';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { AutomationGraphUtilitiesService } from '../../automation-graph-utilities.service';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';

type nodeTypes = 'trigger' | 'task' | 'delay' | 'filter' | 'branch';

@Component({
  selector: 'automata-automation-node',
  templateUrl: './automation-node.component.html',
  styleUrls: ['./automation-node.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    GalaxyPopoverModule,
    MatDividerModule,
    TranslateModule,
    GalaxyPipesModule,
    MatMenuModule,
    GalaxyLoadingSpinnerModule,
    MatCheckbox,
    MatExpansionModule,
    MatTooltipModule,
    GalaxyTooltipModule,
  ],
})
export class AutomationNodeComponent implements OnChanges, OnInit {
  @ViewChild('card', { read: ElementRef }) card: ElementRef<HTMLElement>;

  @Input() type: nodeTypes;
  @Input() icon: string;
  @Input() iconColor: string;
  @Input() selected = false;
  @Input() step: WorkflowStepInterface;
  @Input() precedingSteps: string[];
  @Input() filters: WorkflowStepInterface[];
  @Input() automation: AutomationInterface;
  @Input() enabled = true;
  @Output() goToStepClicked = new EventEmitter<string>();
  @Output() contentClicked = new EventEmitter<void>();

  // "unused", but required to display the AI Icon
  aiIconService = inject(GalaxyAiIconService);

  TRIGGER_STEP_ID = TRIGGER_STEP_ID;
  ERROR_ICON_COLOR = ERROR_ICON_COLOR;
  WARNING_ICON_COLOR = WARNING_ICON_COLOR;

  hasWarning$: Observable<boolean>;
  hasError$: Observable<boolean>;
  displayContents$: Observable<string>[];
  numberWaiting$: Observable<number>;
  stepHasSleep = false;
  showGoToAction = false;
  referencedStepID = '';
  showDebugInfo$: Observable<boolean>;
  runningStates = RunningState;
  isInternal$: Observable<boolean>;
  isDeleting = false;

  groupButtonDisabled = false;
  groupButtonToolTip = '';
  tooltipPositions = [{ ...PopoverPositions.Right }, { ...PopoverPositions.Left }];

  deleteButtonDisabled = false;
  deleteButtonToolTip = '';

  confirmationModal = inject(OpenConfirmationModalService);
  isGrouped = computed(() => this.nodeActionsService.computeIsGrouped(this.step?.id || ''));
  isGroupable = computed(() => this.nodeActionsService.computeIsGroupable(this.step?.id));
  isUnGroupable = computed(() => this.nodeActionsService.computeIsUnGroupable(this.step?.id));

  constructor(
    private readonly displayService: DisplayService,
    private readonly automataService: AutomataService,
    private readonly taskDefinitionsService: TaskDefinitionsService,
    private readonly errorHandler: AutomationErrorHandlerService,
    public readonly nodeActionsService: NodeActionsService,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) readonly partnerId$: Observable<string>,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    protected readonly translateService: TranslateService,
    private readonly automationGraphUtilities: AutomationGraphUtilitiesService,
  ) {}

  isCustomIcon(name: string): boolean {
    return isCustomIcon(name);
  }

  isAutomationStopped(): boolean {
    return IsAutomationStopped(this.automation);
  }

  ngOnChanges(): void {
    if (this.filters?.length > 0 && !!this.automation) {
      this.displayContents$ = this.filters.map((filter) =>
        this.displayService.getActionSubtitle(
          this.automation.namespace,
          filter.taskDefinitionId,
          filter,
          this.automation,
        ),
      );
    } else {
      this.displayContents$ = [];
    }
  }

  ngOnInit(): void {
    this.isInternal$ = this.taskDefinitionsService.internalTriggers$.pipe(
      map(
        (internalTriggers) =>
          !!internalTriggers.find((trigger) => trigger.taskDefinition.id === this.step?.taskDefinitionId),
      ),
    );
    this.showDebugInfo$ = this.route.queryParamMap.pipe(
      take(1),
      map((params) => params.get('debug')),
      map((res) => !!res),
    );
    this.numberWaiting$ = this.partnerId$.pipe(
      switchMap((partnerId) =>
        this.automataService.listCurrentlyWaitingActivities(partnerId, null, this.automation.id, this.step.id, '', 1),
      ),
      map((resp) => resp?.totalResults || 0),
    );
    this.stepHasSleep = WORKFLOW_ACTIONS[this.step?.taskDefinitionId]?.hasSleep;

    if (DirectlyReferencedStepTaskDefinitionIds.find((id) => this.step?.taskDefinitionId === id) !== undefined) {
      this.showGoToAction = true;
      this.referencedStepID = JSON.parse(this.step.data).step_to_jump_to;
    }

    this.hasWarning$ = this.errorHandler.hasWarning(this.step?.id).pipe(
      map((hasWarning) => this.enabled && hasWarning),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.hasError$ = combineLatest([
      this.taskDefinitionsService.checkTaskDefinitionAvailableAtSubscriptionLevel(this.step?.taskDefinitionId),
      this.errorHandler.hasError(this.step?.id),
    ]).pipe(
      map(
        ([isAvailable, hasError]) =>
          this.enabled &&
          (!isAvailable || hasError || (!this?.automation?.triggerStep && this?.automation?.workflow?.length > 0)),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const thisNodeIsPartOfAGroup = this.nodeActionsService.isPartOfGroup(this.step?.id || '', this.automation);
    // disable group button if the node already inside a group or if it's a branch head and there is a group inside one of its branch
    this.groupButtonDisabled =
      thisNodeIsPartOfAGroup || this.automationGraphUtilities.ifContainGroupAsChild(this.step?.id || '');

    this.groupButtonToolTip = thisNodeIsPartOfAGroup
      ? this.translateService.instant('AUTOMATIONS.GROUPING.DISABLE_GROUP_BUTTON_TEXT')
      : this.translateService.instant('AUTOMATIONS.GROUPING.HAS_GROUP_AS_A_CHILD');

    this.deleteButtonDisabled =
      thisNodeIsPartOfAGroup &&
      this.nodeActionsService.findStepsInGroup(this.step?.groupId || '', this.automation).length <= 2;
    this.deleteButtonToolTip = this.deleteButtonDisabled ? 'AUTOMATIONS.GROUPING.DISABLE_DELETE_BUTTON_TEXT' : '';
  }

  protected jumpToStepAction(event): void {
    event.stopPropagation();
    this.goToStepClicked.emit(this.referencedStepID);
  }

  openCurrentlyWaitingDialog(event: any): void {
    event.stopPropagation();
    this.dialog.open(CurrentlyWaitingDialogComponent, {
      data: {
        automation: this.automation,
        workflowStepId: this.step.id,
      },
    });
  }

  copyClicked(): void {
    this.nodeActionsService.copySelected(this.step);
  }

  groupClicked(event: any): void {
    this.nodeActionsService.groupClicked(this.step, event.checked);
  }

  deleteClicked(): void {
    this.confirmationModal
      .openModal({
        type: 'warn',
        title: this.translateService.instant('AUTOMATIONS.DELETE_STEP_MODAL.TITLE'),
        message: this.translateService.instant('AUTOMATIONS.DELETE_STEP_MODAL.DESCRIPTION'),
        confirmButtonText: this.translateService.instant('COMMON.ACTION_LABELS.DELETE'),
        cancelButtonText: this.translateService.instant('COMMON.ACTION_LABELS.CANCEL'),
        width: 540,
      })
      .pipe(first())
      .subscribe((userDidAction) => {
        if (userDidAction === true) {
          this.isDeleting = true;
          this.nodeActionsService
            .delete(this.step, this.precedingSteps)
            .pipe(take(1))
            .subscribe({
              error: () => {
                this.isDeleting = false;
                this.cdr.detectChanges();
              },
            });
        }
      });
  }
}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'automata-automation-node-content',
  standalone: true,
})
export class AutomationNodeContentDirective {}
